import type { CSSProperties, ReactNode } from 'react';
import type { AppFramework, BuildStatus, Environment, EnvVisibility, ExecutionMode, Platform } from '@hangar/shared';
import { humanize } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Badge } from '@/components/ui/badge';
import { pillMotion, type PillMotion } from './statusMotion';

/** Mono 11 px chip for versions, slugs and provider names. */
export function Tag({ children, className, title }: { children: ReactNode; className?: string; title?: string }) {
  return (
    <Badge variant="outline" title={title} className={cn('px-1.5 py-px font-mono text-[11px] font-normal leading-4 text-subtle', className)}>
      {children}
    </Badge>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded-sm border bg-secondary px-1.5 py-0.5 font-mono text-[11px] leading-none text-subtle">{children}</kbd>;
}

export type Tone = 'succeeded' | 'failed' | 'running' | 'queued' | 'awaiting' | 'canceled' | 'subtle' | 'accent';

const TONE_TEXT: Record<Tone, string> = {
  succeeded: 'text-succeeded',
  failed: 'text-failed',
  running: 'text-running',
  queued: 'text-queued',
  awaiting: 'text-awaiting',
  canceled: 'text-canceled',
  subtle: 'text-subtle',
  accent: 'text-primary',
};

export function Dot({ tone = 'subtle', pulse, className }: { tone?: Tone; pulse?: boolean; className?: string }) {
  return <span aria-hidden className={cn('inline-block size-2 shrink-0 rounded-full bg-current', TONE_TEXT[tone], pulse && 'pulse', className)} />;
}

/** A small spinning ring in the current color: something is being worked on right now (styles/motion.css `.spin`). */
export function Spinner({ className }: { className?: string }) {
  return <span aria-hidden className={cn('spin inline-block size-2.5 shrink-0 rounded-full border-[1.5px] border-current border-t-transparent', className)} />;
}

/** Environment pill: color from the API (`environment.color`), falling back to the kind's token. */
export function EnvPill({ env, className }: { env: Pick<Environment, 'name' | 'kind' | 'color' | 'isProtected'> | { slug: string; name?: string; kind: string; color?: string | null; isProtected?: boolean }; className?: string }) {
  const name = 'name' in env && env.name ? env.name : 'slug' in env ? env.slug : '';
  const style = { '--pill': env.color ?? `var(--env-${env.kind})` } as CSSProperties;
  return (
    <Badge
      variant="outline"
      style={style}
      title={env.isProtected ? `${name} · protected` : name}
      className={cn('gap-1.5 [color:var(--pill)] [border-color:color-mix(in_oklab,var(--pill)_35%,transparent)] [background:color-mix(in_oklab,var(--pill)_12%,transparent)]', className)}
    >
      <span aria-hidden className="size-1.5 rounded-full [background:var(--pill)]" />
      {name}
    </Badge>
  );
}

/** Build statuses (SPEC §11.1) → tone. Provider statuses from P1 webhooks keep their rows. */
export const BUILD_STATUS_TONE: Record<BuildStatus, Tone> = {
  pending_approval: 'awaiting',
  awaiting_client: 'awaiting',
  queued: 'queued',
  dispatching: 'running',
  provider_queued: 'awaiting',
  running: 'running',
  verifying: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
  errored: 'failed',
  canceled: 'canceled',
  timed_out: 'failed',
  expired: 'canceled',
};

const STATUS_TONE: Record<string, Tone> = {
  ...BUILD_STATUS_TONE,
  finished: 'succeeded',
  cancelled: 'canceled',
  'in-progress': 'running',
  in_progress: 'running',
  'in-queue': 'queued',
  pending: 'queued',
  awaiting_approval: 'awaiting',
  next: 'awaiting', // Getting started: the next step to do
  passed: 'succeeded',
  warning: 'awaiting',
  error: 'failed',
  skipped: 'subtle',
  active: 'succeeded',
  degraded: 'awaiting',
  disabled: 'canceled',
  // shadow runs (P3): a match, a difference, or a provider build no pipeline step reported
  match: 'succeeded',
  differs: 'failed',
  not_shadowed: 'awaiting',
  // store submissions (P29)
  submitting: 'running',
  processing: 'running',
  in_review: 'awaiting',
  ready_to_publish: 'awaiting',
  published: 'succeeded',
  rejected: 'failed',
};

export const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['succeeded', 'failed', 'errored', 'canceled', 'timed_out', 'expired']);
export { BUILD_WAITING_STATUSES, LIVE_STATUSES, WAITING_STATUSES } from './statusMotion';

/** Tinted pill classes per tone (StatusPill, and other dot + label pills). */
export const TONE_PILL: Record<Tone, string> = {
  succeeded: 'border-succeeded/30 bg-succeeded/10 text-succeeded',
  failed: 'border-failed/30 bg-failed/10 text-failed',
  running: 'border-running/30 bg-running/10 text-running',
  queued: 'border-border bg-secondary text-muted-foreground',
  awaiting: 'border-awaiting/30 bg-awaiting/10 text-awaiting',
  canceled: 'border-border bg-secondary text-canceled',
  subtle: 'border-border bg-secondary text-subtle',
  accent: 'border-primary/30 bg-primary/10 text-primary',
};

export const toneOfStatus = (status: string): Tone => STATUS_TONE[status.toLowerCase()] ?? 'queued';

interface StatusPillProps {
  status: string;
  className?: string;
  label?: ReactNode;
  size?: 'sm' | 'md';
  /** History (a timeline entry): the same pill without motion. */
  still?: boolean;
  /** `build` for a build's own current status: it also pulses while it waits in Hangar's queue or for approval. */
  motion?: PillMotion;
}

/**
 * Status badge: dot + label, tinted (never color alone). A current status moves: a spinner while it is worked on, a
 * pulse while it waits (components/statusMotion.ts).
 */
export function StatusPill({ status, className, label, size = 'sm', still, motion }: StatusPillProps) {
  const tone = toneOfStatus(status);
  const errored = status === 'errored';
  const moving = pillMotion(status, motion, still);
  return (
    <Badge
      variant="outline"
      data-status={status}
      className={cn('gap-1.5 font-mono font-normal', size === 'sm' ? 'text-[11px] leading-4' : 'px-2.5 py-1 text-xs leading-4', TONE_PILL[tone], errored && 'bg-transparent', className)}
    >
      {moving === 'spin' ? <Spinner className={TONE_TEXT[tone]} /> : <Dot tone={tone} pulse={moving === 'pulse'} />}
      {label ?? humanize(status)}
    </Badge>
  );
}

export const FRAMEWORK_LABEL: Record<AppFramework, string> = {
  expo: 'Expo',
  react_native: 'React Native',
  ios_native: 'iOS native',
  android_native: 'Android native',
  flutter: 'Flutter',
};

export const FRAMEWORK_ICON: Record<AppFramework, string> = {
  expo: 'bolt',
  react_native: 'bolt',
  ios_native: 'phone_iphone',
  android_native: 'android',
  flutter: 'apps',
};

/**
 * Framework chip. `labelClassName` lets a narrow container hide the visible label (e.g. `@max-[21rem]:hidden`) and keep
 * the glyph; a plain `sr-only` twin keeps the name for screen readers either way.
 */
export function FrameworkBadge({ framework, className, labelClassName }: { framework: AppFramework; className?: string; labelClassName?: string }) {
  const label = FRAMEWORK_LABEL[framework];
  return (
    <Badge variant="outline" className={className} title={label}>
      <Icon name={FRAMEWORK_ICON[framework]} size={14} className={framework === 'android_native' ? 'text-android' : undefined} />
      {labelClassName ? (
        <>
          <span aria-hidden className={labelClassName}>
            {label}
          </span>
          <span className="sr-only">{label}</span>
        </>
      ) : (
        label
      )}
    </Badge>
  );
}

/** Platform glyph: Android robot tinted green, Apple neutral (design/DESIGN.md). */
export function PlatformIcon({ platform, size = 18, className, withLabel }: { platform: Platform; size?: number; className?: string; withLabel?: boolean }) {
  const icon = <Icon name={platform === 'android' ? 'android' : 'phone_iphone'} size={size} label={withLabel ? undefined : platform === 'android' ? 'Android' : 'iOS'} className={cn(platform === 'android' ? 'text-android' : 'text-muted-foreground', className)} />;
  if (!withLabel) return icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
      {icon}
      {platform === 'android' ? 'Android' : 'iOS'}
    </span>
  );
}

const VISIBILITY: Record<EnvVisibility, { icon: string; className: string; title: string }> = {
  plain: { icon: 'visibility', className: 'border-border bg-secondary text-muted-foreground', title: 'Plain · readable by any member' },
  sensitive: { icon: 'visibility_off', className: 'border-awaiting/30 bg-awaiting/10 text-awaiting', title: 'Sensitive · masked, reveal is audited' },
  secret: { icon: 'lock', className: 'border-failed/30 bg-failed/10 text-failed', title: 'Secret · write-only, never returned' },
};

export function VisibilityBadge({ visibility, className }: { visibility: EnvVisibility; className?: string }) {
  const v = VISIBILITY[visibility];
  return (
    <Badge variant="outline" title={v.title} className={cn('gap-1 font-mono text-[11px] font-normal leading-4', v.className, className)}>
      <Icon name={v.icon} size={13} />
      {visibility}
    </Badge>
  );
}

const SCOPE_LABEL: Record<string, string> = { org: 'org', project: 'project', app: 'app', request: 'override', system: 'system' };

/** Precedence chip for env resolution: scope (+ environment, + platform). */
export function ScopeChip({ scope, environment, platform, className, muted }: { scope: string; environment?: string | null; platform?: string | null; className?: string; muted?: boolean }) {
  const parts = [SCOPE_LABEL[scope] ?? scope, environment ?? undefined, platform ?? undefined].filter(Boolean);
  return (
    <Tag className={cn(muted ? 'line-through opacity-70' : scope === 'request' ? 'text-primary' : scope === 'system' ? 'text-running' : 'text-foreground', className)} title={muted ? 'Overridden by a higher-precedence definition' : undefined}>
      {parts.join(' + ')}
    </Tag>
  );
}

/** How a build runs, in the words a client admin uses (the build screens add the service name: features/builds/buildLabels.ts). */
export const EXECUTION_MODE_LABEL: Record<ExecutionMode, string> = { provider_api: "Built by Hangar's build service", ci_client: 'From your CI', hangar_runner: 'Built by Hangar' };

export function ExecutionModeTag({ mode, className }: { mode: ExecutionMode; className?: string }) {
  return <Tag className={cn('text-muted-foreground', className)}>{EXECUTION_MODE_LABEL[mode]}</Tag>;
}

/** definedIn badge: config_file resources are read-only (HGR-MANAGED-BY-CONFIG). */
export function DefinedInBadge({ definedIn, className }: { definedIn: 'ui' | 'config_file'; className?: string }) {
  return definedIn === 'config_file' ? (
    <Badge variant="outline" title="Managed by hangar.yml (sync from the repository to change it)" className={cn('gap-1 font-mono text-[11px] font-normal text-muted-foreground', className)}>
      <Icon name="lock" size={12} />
      hangar.yml
    </Badge>
  ) : (
    <Tag className={className}>ui</Tag>
  );
}
